import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FadeUp } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#121210] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 focus:shadow-[0_0_15px_rgba(16,185,129,0.1)] transition-all duration-200";

export default function ChangePassword() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    return (
      <main data-testid="change-password-page" className="pt-28 md:pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-slate-400">You must be signed in to change your password.</p>
          <Link to="/auth" className="text-emerald-400 hover:text-emerald-300 text-sm">Sign in</Link>
        </div>
      </main>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("New passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setSuccess(true);
      setTimeout(() => navigate("/"), 3000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="change-password-page" className="pt-28 md:pt-16 min-h-screen flex items-center justify-center relative overflow-hidden">
      <Seo title="Change password — HighRank" siteName="HighRank" description="Update your HighRank account password." />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[30rem] h-[18rem] rounded-full bg-emerald-500/10 blur-[110px]" />

      <FadeUp className="relative w-full max-w-md px-4 py-10">
        <div className="text-center mb-8">
          <Link to="/" data-testid="change-logo" className="inline-flex mb-6" aria-label="HighRank.space home">
            <Logo />
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100">
            Change password
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Signed in as <span className="text-slate-300">{user.email}</span>
          </p>
        </div>

        {success ? (
          <div
            data-testid="change-password-success"
            className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-7 text-center space-y-4"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-slate-200 font-medium">Password changed!</p>
            <p className="text-sm text-slate-500">Redirecting you home…</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="change-password-form" className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 space-y-4">
            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Current password</label>
              <input
                data-testid="change-current-password-input"
                type="password"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                required
                placeholder="Your current password"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">New password</label>
              <input
                data-testid="change-new-password-input"
                type="password"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                required
                placeholder="At least 8 characters"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Confirm new password</label>
              <input
                data-testid="change-confirm-input"
                type="password"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                required
                placeholder="Re-enter new password"
                className={inputCls}
              />
            </div>
            {error && (
              <p data-testid="change-password-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="change-password-submit"
              disabled={submitting}
              className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-full py-3 transition-all duration-200 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]"
            >
              {submitting ? "Saving…" : "Update password"}
            </button>
            <p className="text-center text-sm text-slate-600">
              <Link to="/" data-testid="change-cancel-link" className="text-slate-500 hover:text-slate-300 transition-colors">
                Cancel
              </Link>
            </p>
          </form>
        )}
      </FadeUp>
    </main>
  );
}
